import { useNavigate } from 'react-router';

import {
  AgreementLegalContextSettings,
  AgreementConditionsSettings,
  AgreementJurisdictionSettings,
  AgreementLiabilitySettings,
} from 'src/containers/Escrow/Agreement';
import { useSoftwareEscrow } from 'src/context/software-escrow';
import {
  AgreementContextProvider,
  useAgreementSuggestChanges,
} from 'src/context/suggest-changes/AgreementSuggestChangesContext';
import { JurisdictionEnum, LiabilityEnum, type IAgreement } from 'src/graphql/schema';
import {
  SoftwareOnboardingLayout,
  SoftwareOnboardingHeader,
  SoftwareOnboardingMain,
  SoftwareOnboardingFooter,
} from 'src/components/SoftwareOnboarding';
import { ExitAlertDialog } from 'src/pages/SoftwareEscrowRequestChanges/ui';

import { SubmitChangesDialog } from './ui';

const Content = () => {
  const {
    softwareEscrow: { id, agreement },
  } = useSoftwareEscrow();
  const navigate = useNavigate();
  const {
    state,
    updateConditions,
    updateContext,
    updateJurisdiction,
    updateLiability,
    updateOtherContext,
    updateOtherCondition,
  } = useAgreementSuggestChanges();

  const onDiscardChanges = () => navigate(`/software-escrows/${id}/settings/agreement`);

  return (
    <SoftwareOnboardingLayout>
      <SoftwareOnboardingHeader justify="end">
        <ExitAlertDialog onDiscard={onDiscardChanges} />
      </SoftwareOnboardingHeader>
      <SoftwareOnboardingMain
        subtitle="Request changes to agreement information:"
        title="Details"
        noteText="These selections will tailor the escrow agreement to your specific arrangement."
        classes={{
          note: 'not-italic [&>span]:hidden',
          subtitle: 'font-medium',
        }}
      >
        <div className="w-[580px] max-w-full mx-auto space-y-4">
          <AgreementLegalContextSettings
            onUpdate={({ legalContext }) =>
              updateContext({
                values: legalContext || [],
                hasChanges: hasChanges(agreement?.legalContext ?? [], legalContext || []),
              })
            }
            onOtherChange={(legalContextOther) =>
              updateOtherContext({
                value: legalContextOther,
                hasChanges: hasChanges(agreement?.legalContextOther ?? '', legalContextOther),
              })
            }
            legalContext={state.data.context}
            legalContextOther={state.data.otherContext}
          />
          <AgreementConditionsSettings
            title="Release events"
            description={`Select from the standard terms, or define the specific conditions under which the beneficiary can request the release of the escrow materials.\n\nChoose one, or all that are applicable.`}
            onUpdate={({ conditions }) =>
              updateConditions({
                values: conditions || [],
                hasChanges: hasChanges(agreement?.conditions ?? [], conditions || []),
              })
            }
            onOtherChange={(conditionsOther) =>
              updateOtherCondition({
                value: conditionsOther,
                hasChanges: hasChanges(agreement?.conditionsOther ?? '', conditionsOther),
              })
            }
            conditions={state.data.conditions}
            conditionsOther={state.data.conditionsOther}
          />
          <AgreementJurisdictionSettings
            onUpdate={({ jurisdiction }) =>
              updateJurisdiction({
                value: jurisdiction as JurisdictionEnum,
                hasChanges: hasChanges(agreement?.jurisdiction ?? '', jurisdiction ?? ''),
              })
            }
            jurisdiction={state.data.jurisdiction}
          />
          <AgreementLiabilitySettings
            onUpdate={({ liability }) =>
              updateLiability({
                value: liability as LiabilityEnum,
                hasChanges: hasChanges(agreement?.liability ?? '', liability ?? ''),
              })
            }
            liability={state.data.liability}
          />
        </div>
      </SoftwareOnboardingMain>
      <SoftwareOnboardingFooter justify="end">
        <SubmitChangesDialog />
      </SoftwareOnboardingFooter>
    </SoftwareOnboardingLayout>
  );
};

function hasChanges<T extends string | string[]>(oldValue: T, newValue: T) {
  if (Array.isArray(newValue) && Array.isArray(oldValue)) {
    const sortedNew = [...newValue].sort();
    const sortedOld = [...oldValue].sort();

    if (sortedNew.length !== sortedOld.length) return true;
    for (let i = 0; i < sortedNew.length; i++) {
      if (sortedNew[i] !== sortedOld[i]) return true;
    }
    return false;
  }
  return newValue !== oldValue;
}

const SoftwareEscrowRequestChangesAgreement = () => {
  const {
    softwareEscrow: { id: escrowId, agreement, policy },
  } = useSoftwareEscrow();

  return (
    <AgreementContextProvider
      escrowId={escrowId}
      agreement={agreement as IAgreement}
      canCreateAgreementSuggestedChange={policy.createAgreementSuggestedChange}
    >
      <Content />
    </AgreementContextProvider>
  );
};

export default SoftwareEscrowRequestChangesAgreement;
